"use client"

import { WebSocketIndicator } from "@/components/common/web-socket/web-socket-indicator"
import { useAuth } from "@/features/auth/hooks/useAuth"
import { Avatar, AvatarFallback, AvatarImage } from "@ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@ui/dropdown-menu"
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@ui/sidebar"
import {
  ChevronsUpDown,
  CreditCard,
  LogOut,
  MessageSquare,
  ShieldCheck,
  Sparkles,
  UserCog,
} from "lucide-react"
import { useNavigate } from "react-router-dom"

// Função para gerar cor baseada no nome do usuário
const getAvatarColor = (name: string) => {
  // Lista de cores para avatares (HSL)
  const colors = [
    "bg-primary text-primary-foreground",
    "bg-blue-500 text-white",
    "bg-green-500 text-white",
    "bg-amber-500 text-white",
    "bg-purple-500 text-white",
    "bg-rose-500 text-white",
    "bg-cyan-500 text-white",
    "bg-indigo-500 text-white",
  ]

  // Usar o código do nome para selecionar uma cor
  const charCodeSum = name.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0)
  const colorIndex = charCodeSum % colors.length

  return colors[colorIndex]
}

// Função para gerar iniciais do nome
const getInitials = (name: string) => {
  const nameParts = name.split(" ")
  if (nameParts.length === 1) return nameParts[0].substring(0, 2).toUpperCase()
  return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase()
}

export function NavUser({
  user,
}: {
  user: {
    name: string
    email: string
    avatar: string
  }
}) {
  const { isMobile } = useSidebar()
  const avatarColor = getAvatarColor(user.name)
  const initials = getInitials(user.name)
  const { logout, user: authUser } = useAuth()
  const navigate = useNavigate()

  // Verificar se o usuário tem role de admin
  const isAdmin = authUser?.roles?.includes("admin") || false

  const logoutUser = () => {
    logout()
    navigate("/auth/login")
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="relative">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={user.avatar} alt={user.name} />
                  <AvatarFallback className={`rounded-lg ${avatarColor}`}>
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="absolute -top-1 -right-1">
                  <WebSocketIndicator showText={false} size="sm" />
                </div>
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{user.name}</span>
                <span className="truncate text-xs">{user.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={user.avatar} alt={user.name} />
                  <AvatarFallback className={`rounded-lg ${avatarColor}`}>
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{user.name}</span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem>
                <Sparkles />
                Upgrade to Pro
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              {isAdmin && (
                <DropdownMenuItem onClick={() => navigate("/admin")}>
                  <ShieldCheck />
                  Admin
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => navigate("/settings")}>
                <UserCog />
                Minha Conta
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/settings/billing")}>
                <CreditCard />
                Assinatura
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/settings/channels")}>
                <MessageSquare />
                Canais
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => logoutUser()}>
              <LogOut />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
